import React, { useState, useEffect, useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import DataTable from "react-data-table-component";
import axiosInstance from "../axiosConfig";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { AuthContext } from "../AuthContext";
import Select from "react-select";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";

function UserTicketsView() {
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState({ fromDate: "", toDate: "" });
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // WorkerTicket table state
  const [formData, setFormData] = useState({
    worker_id: "",
    worker_name: "",
    date: "",
    desc: "",
  });

  const [workers, setWorkers] = useState([]);

  // Fetch Workers for Dropdown
  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const response = await axiosInstance.get("/api/workers-list");
        const workerOptions = response.data.map((w) => ({
          value: JSON.stringify({ id: w.EmployeeID, name: w.Name }), 
          label: `${w.Name} (${w.EmployeeID})`,
        }));
        setWorkers(workerOptions);
      } catch (error) {
        console.error("Error fetching workers:", error);
      }
    };
    fetchWorkers();
  }, []);

  // Update isMobile on resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ NEW API: Fetch tickets assigned by this supervisor using supervisor_id
  const fetchTickets = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`/api/worker-tickets/supervisor/${user.EmployeeID}`);
      setTickets(response.data);
      setFilteredTickets(response.data);
    } catch (error) {
      console.error("Error fetching worker tickets for supervisor:", error);
    }
  }, [user?.EmployeeID]);

  useEffect(() => {
    if (user?.EmployeeID) {
      fetchTickets();
    }
  }, [fetchTickets, user?.EmployeeID]);

  // Apply search and date filters
  const applyFilters = useCallback(() => {
    const { fromDate, toDate } = filters;
    const filtered = tickets.filter((ticket) => {
      const ticketDate = ticket.date ? new Date(ticket.date) : null;
      const matchesSearch =
        searchText === "" ||
        [ticket.id, ticket.worker_id, ticket.worker_name, ticket.desc]
          .join(" ")
          .toLowerCase()
          .includes(searchText.toLowerCase());
          
      const matchesDate =
        (!fromDate || (ticketDate && ticketDate >= new Date(fromDate))) &&
        (!toDate || (ticketDate && ticketDate <= new Date(toDate).setHours(23, 59, 59, 999)));
      return matchesSearch && matchesDate;
    });
    setFilteredTickets(filtered);
  }, [tickets, filters, searchText]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Extracts worker_id and worker_name from selected value
  const handleResponsibleChange = (selectedOption) => {
    if (selectedOption) {
      const workerDetails = JSON.parse(selectedOption.value);
      setFormData((prev) => ({
        ...prev,
        worker_id: workerDetails.id,
        worker_name: workerDetails.name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        worker_id: "",
        worker_name: "",
      }));
    }
  };

  // ✅ NEW API: Submit form data to the new /api/assign-ticket endpoint
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!formData.date) {
        Swal.fire("Error", "Date is required", "error");
        return;
      }

      const requestData = {
        worker_id: formData.worker_id,
        worker_name: formData.worker_name,
        date: formData.date,
        desc: formData.desc,
        supervisor_id: user?.EmployeeID, 
      };

      await axiosInstance.post("/api/assign-ticket", requestData);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Ticket assigned successfully!",
        timer: 2000,
        showConfirmButton: false,
      });

      setShowModal(false);
      fetchTickets(); 

      setFormData({
        worker_id: "",
        worker_name: "",
        date: "",
        desc: "",
      });

    } catch (error) {
      console.error("Error assigning worker ticket:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to assign ticket! Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Columns matched to WorkerTicket database keys
  const columns = [
    { name: "Ticket ID", selector: (row) => row.id, sortable: true, width: "100px" },
    { name: "Worker Code", selector: (row) => row.worker_id, sortable: true, width: "130px" },
    {
      name: "Worker Name",
      selector: (row) => (
        <span data-bs-toggle="tooltip" data-bs-placement="top" title={row.worker_name}>
          {row.worker_name}
        </span>
      ),
      sortable: true, width: "180px"
    },
    {
      name: "Description",
      selector: (row) => (
        <span data-bs-toggle="tooltip" data-bs-placement="top" title={row.desc}>
          {row.desc}
        </span>       
      ),
      sortable: false, wrap: true
    },
    {
      name: "Assigned Date",
      selector: (row) => 
        row.date ? new Date(row.date).toLocaleString("en-GB", {
          timeZone: "Asia/Kolkata",
          hour12: false,
        }) : "N/A",
      sortable: true, width: "180px" 
    },
    { name: "Updated By", selector: (row) => row.updated_by || "N/A", sortable: true, width: "130px" }
  ];

  return (
    <>
      <div className="d-flex dashboard">
        <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
          <Sidebar />
        </div>
        <div className="flex-grow-1">
          <Header toggleSidebar={() => setIsSidebarVisible(!isSidebarVisible)} isSidebarVisible={isSidebarVisible} />
          <div id="alertPlaceholder"></div>
          
          <main className="main-container p-4" style={{ height: "-webkit-fill-available" }}>
            
            {/* TOP HEADER */}
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <h2 className="mb-2 mb-md-0 fw-bold text-dark">Worker's Ticket Management</h2>
              <div className="d-flex flex-wrap gap-2">
                <button 
                  className="btn btn-primary shadow-sm d-flex align-items-center gap-1" 
                  onClick={() => setShowModal(true)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-plus-circle" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4"/>
                  </svg>
                  Assign Ticket to Worker
                </button>

                <button 
                  className="btn btn-outline-success shadow-sm d-flex align-items-center gap-1 bg-white" 
                  onClick={() => window.location.reload()}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-clockwise" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                    <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                  </svg>
                  Reload
                </button>
              </div>
            </div>

            {/* FILTERS SECTION */}
            <div className="card border-0 shadow-sm rounded-4 mb-4" style={{ backgroundColor: "#f8f9fa" }}>
              <div className="card-body p-3">
                <div className="row g-3 align-items-center">
                  <div className="col-12 col-md-4">
                    <div className="input-group shadow-sm">
                      <span className="input-group-text bg-white border-end-0 text-muted">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0 bg-white"
                        placeholder="Search tickets..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-auto d-flex align-items-center gap-2">
                    <label className="fw-semibold text-secondary mb-0">From:</label>
                    <input
                      type="date"
                      className="form-control shadow-sm"
                      value={filters.fromDate}
                      onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                    />
                  </div>
                  <div className="col-12 col-md-auto d-flex align-items-center gap-2">
                    <label className="fw-semibold text-secondary mb-0">To:</label>
                    <input
                      type="date"
                      className="form-control shadow-sm"
                      value={filters.toDate}
                      onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                    />
                  </div>
                  {(filters.fromDate || filters.toDate || searchText) && (
                    <div className="col-12 col-md-auto">
                      <button 
                        className="btn btn-link text-danger text-decoration-none p-0" 
                        onClick={() => { setFilters({ fromDate: "", toDate: "" }); setSearchText(""); }}
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* DATA TABLE SECTION */}
            { !isMobile ? (
              <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
                <DataTable 
                  columns={columns} 
                  data={filteredTickets} 
                  pagination 
                  highlightOnHover 
                  pointerOnHover 
                  customStyles={{
                    headRow: { style: { backgroundColor: '#f1f5f9', fontWeight: 'bold' } }
                  }}
                />
              </div>
            ) : (
              <div className="table-mobile">
                {filteredTickets.map((ticket, idx) => (
                  <div key={idx} className="card shadow-sm mb-3 border-0 rounded-4">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="card-title mb-0 fw-bold text-primary">#{ticket.id}</h5>
                      </div>
                      <p className="card-text mb-1"><strong>Worker Code:</strong> {ticket.worker_id}</p>
                      <p className="card-text mb-1"><strong>Worker Name:</strong> {ticket.worker_name}</p>
                      <p className="card-text mb-1"><strong>Description:</strong> {ticket.desc}</p>
                      <p className="card-text mb-2 text-muted small">
                        <strong>Assigned Date:</strong> {ticket.date ? new Date(ticket.date).toLocaleString("en-GB") : "N/A"}
                      </p>
                      <p className="card-text mb-0 text-muted small">
                        <strong>Updated By:</strong> {ticket.updated_by || "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Raise Ticket Modal via React Portal */}
            {showModal && createPortal(
              <div
                className="modal show d-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "rgba(0,0,0,0.6)",
                  backdropFilter: "blur(4px)",
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 999999,
                }}
              >
                <div className="modal-dialog modal-lg m-0 w-100" style={{ maxWidth: "600px", padding: "15px" }}>
                  <div className="modal-content shadow-lg border-0 rounded-4">
                    <div className="modal-header bg-primary text-white border-0 rounded-top-4">
                      <h5 className="modal-title fw-bold d-flex align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-ticket-detailed me-2" viewBox="0 0 16 16">
                          <path d="M4 5.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5Zm0 5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5ZM5 7a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2H5Z" />
                          <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5V6a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 11.5V10a.5.5 0 0 1 .5-.5 1.5 1.5 0 1 0 0-3A.5.5 0 0 1 0 6V4.5ZM1.5 4a.5.5 0 0 0-.5.5v1.05a2.5 2.5 0 0 1 0 4.9v1.05a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-1.05a2.5 2.5 0 0 1 0-4.9V4.5a.5.5 0 0 0-.5-.5h-13Z" />
                        </svg>
                        Assign Ticket to Worker
                      </h5>
                      <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                    </div>
                    <form onSubmit={handleFormSubmit}>
                      <div className="modal-body p-4 bg-light">
                        
                        {/* Worker Selection Dropdown */}
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-secondary">Select Worker</label>
                          <Select
                            options={workers}
                            onChange={handleResponsibleChange}
                            placeholder="Search Worker by Name or Code..."
                            isClearable
                            isSearchable
                            className="shadow-sm"
                            required
                          />
                        </div>

                        {/* Date Input */}
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-secondary">Date</label>
                          <input
                            type="datetime-local"
                            name="date"
                            value={formData.date}
                            onChange={handleFormChange}
                            className="form-control shadow-sm"
                            required
                          />
                        </div>

                        {/* Description Input */}
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-secondary">Task Description</label>
                          <textarea
                            name="desc"
                            value={formData.desc}
                            onChange={handleFormChange}
                            className="form-control shadow-sm"
                            placeholder="Describe the task details for the worker..."
                            rows="4"
                            required
                          ></textarea>
                        </div>

                      </div>
                      <div className="modal-footer border-0 bg-light rounded-bottom-4">
                        <button type="button" className="btn btn-outline-secondary px-4 rounded-pill fw-medium" onClick={() => setShowModal(false)}>
                          Cancel
                        </button>
                        <button type="submit" className="btn btn-primary px-4 rounded-pill shadow-sm fw-medium" disabled={submitting}>
                          {submitting ? (
                            <><span className="spinner-border spinner-border-sm me-2"></span>Assigning...</>
                          ) : (
                            "Assign Ticket"
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>,
              document.body 
            )}
          </main>
        </div>
      </div>
    </>
  );
}

export default UserTicketsView;